import React, { useEffect, useState } from 'react'
import { useAppSelector, useAppDispatch } from '../../app/redux/hooks'
import useSeqLogger from '../../app/seqLog'
import { specialMappedQuestions, translatedAnswers } from '../../app/api/apiMapping'
import {
    selectActivatedLanguage,
    setGlobalLangauage,
    selectGlobalLanguage,
    selectGlobalLanguageState,
} from '../../app/redux/globalLanguage.Slice'

import {
    resetDependentAnswersForward,
    updateAnswer,
    addFiles,
    submitApplication,
    selectStateQuestion,
    clearDisplayedFromStep,
    setQuestionDisplayed,
    selectApplicationContentFromLanguageSupport,
    setApplicationContent,
    setStep,
    selectStepFromLanguageSupport,
    setQuestion,
    selectQuestionFromLanguageSupport,
} from '../../app/redux/questions.Slice'

import { useOutletContext } from 'react-router-dom'
import PTSFormRenderer from './PTSFormRenderer'

import {
    hasQuestionValidationError,
    buildStepCategoryGroupQuestionStructure,
    createApiDataObject,
    focusElement,
    getGroupCheckIds,
    useFormStatusModal,
    usePTSPageTitle,
    FormStatusMessagesScreenReader,
    preparePreviewData,
    findVisibleQuestionsWithValidationErrors,
} from 'optimized-react-component-library-xyz123'
import { getSessionId } from '../../app/api/apiHandler'

const PTSForm = () => {
    const dispatch = useAppDispatch()
    const [openFormStatusModal, closeFormStatusModal] = useOutletContext<[() => void, () => void]>()

    // Global state
    const {
        formStatus: globalFormStatus,
        applicationContent: globalApplicationContent,
        questions: globalQuestions,
        steps: globalSteps,
    } = useAppSelector(selectStateQuestion)

    const globalLanguageState = useAppSelector(selectGlobalLanguageState)
    const globalActivatedLanguage = useAppSelector(selectActivatedLanguage)
    const globalThankYouPageContent = globalApplicationContent?.textblocks?.find(block => block.id === 'ThankYouBlock')

    // Local state
    const [appConfig, setAppConfig] = useState<any>({})
    const [activeStep, setActiveStep] = useState(0)
    const [showPreview, setShowPreview] = useState(false)
    const [validationErrorsList, setValidationErrorsList] = useState<number[]>([])
    const [stepCategoryGroupStructure, setStepCategoryGroupStructure] = useState<any[]>(
        buildStepCategoryGroupQuestionStructure(globalQuestions, globalSteps, validationErrorsList)
    )
    const [previewPageData, setPreviewPageData] = useState<any[]>([])

    /** Automatiskt information loggning */
    const { logToSeq } = useSeqLogger()
    const sessionId = getSessionId()
    const [hasStartedTyping, setHasStartedTyping] = useState(false)

    useEffect(() => {
        {
            activeStep > 0 &&
                logToSeq({
                    message: `SessionId: ${sessionId}, Steg ${activeStep}, Status: ${globalFormStatus}, Started typing: ${hasStartedTyping}`,
                    level: 'Information',
                })
        }
    }, [logToSeq, activeStep, hasStartedTyping, validationErrorsList])

    /**
     * Hämtar applikationens konfigurationsfil (config.json) vid första sidladdningen
     * och sparar resultatet i local state. Körs endast en gång vid initial render.
     */
    useEffect(() => {
        fetch('/config.json')
            .then(response => response.json())
            .then(data => setAppConfig(data))
    }, [])

    /**
     * Hämtar språkberoende formulärinnehåll baserat på det aktuella aktiva språket
     */
    const translatedFormContent = useAppSelector(state => ({
        applicationContent: selectApplicationContentFromLanguageSupport(state, globalActivatedLanguage),
        steps: selectStepFromLanguageSupport(state, globalActivatedLanguage),
        questions: selectQuestionFromLanguageSupport(state, globalActivatedLanguage),
        globalLanguage: selectGlobalLanguage(state, globalActivatedLanguage),
    }))

    /**
     * Synkar språkberoende innehåll i formuläret med valt språk.
     * Körs varje gång som det aktiva språket ändras i applikationen.
     */
    useEffect(() => {
        dispatch(setApplicationContent(translatedFormContent.applicationContent))
        dispatch(setStep(translatedFormContent.steps))
        dispatch(setQuestion(translatedFormContent.questions))
        dispatch(setGlobalLangauage(translatedFormContent.globalLanguage))
    }, [globalActivatedLanguage])

    /**
     * Bygger och sätter data för förhandsgranskningssidan när användaren
     * går in i förhandsvisningsläget. Körs endast när showPreview ändras
     * till true eller när det aktiva språket ändras på förhandsgranskningssidan.
     */
    useEffect(() => {
        if (!showPreview) return

        const data = preparePreviewData(globalSteps, globalQuestions)
        setPreviewPageData(data)
    }, [showPreview, globalActivatedLanguage])

    /**
     * Sätter sidtiteln dynamiskt baserat på formulärets status, aktivt steg
     * och det språkstyrda innehållet. Används för att hålla dokumenttiteln
     * uppdaterad genom hela flödet.
     */
    usePTSPageTitle({
        formStatus: globalFormStatus,
        activeStep,
        applicationContent: globalApplicationContent,
        thankYouBlock: globalThankYouPageContent,
        steps: globalSteps,
    })

    /**
     * Övervakar formulärets status och öppnar eller stänger modalfönster
     * baserat på aktuellt formStatus.
     */
    useFormStatusModal(globalFormStatus, openFormStatusModal, closeFormStatusModal)

    /**
     * Markerar en fråga som visad (isDisplayed = true) när den renderas i UI:t.
     * Detta används för att hålla reda på vilka frågor som har exponerats för
     * användaren.
     */
    const markQuestionAsDisplayed = (questionObject: any) => {
        dispatch(setQuestionDisplayed({ id: questionObject.id, isDisplayed: true }))
    }

    /**
     * Hanterar förändringar i ett specifikt frågefält:
     * - uppdaterar svaret i global state
     * - hanterar eventuell filuppladdning
     * - uppdaterar validerings-fel-listan om felet inte längre kvarstår
     * - triggar logik för att rensa svar framåt i formuläret som är beroende
     * av frågans svar
     *
     */
    const handleQuestionInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
        questionObject: {
            id: number
            questionType: string
            validationType?: string[]
            [key: string]: any
        }
    ) => {
        const { id, questionType, validationType } = questionObject
        const value = e.target.value
        setHasStartedTyping(true)

        // Uppdaterar svaret i global state
        dispatch(updateAnswer({ id, answer: value }))

        // Hanterar filuppladdning i global state (gäller bara frågor av typen AddFiles)
        if (questionType === 'AddFiles') {
            const files = (e.target as HTMLInputElement).files
            dispatch(addFiles({ id, files }))
        }

        // Om frågan tidigare haft valideringsfel: kontrollera om felet finns kvar
        if (validationErrorsList.includes(id)) {
            const updatedQuestion = {
                ...questionObject,
                answer: value,
                isTouched: true,
            }

            const hasError = hasQuestionValidationError(updatedQuestion, globalQuestions)

            // Om frågan (och ev. gruppfrågor) inte längre har fel – ta bort deras id:n från fel-listan
            if (!hasError) {
                const idsToRemoveSet = new Set<number>([updatedQuestion.id, ...getGroupCheckIds(validationType)])

                setValidationErrorsList(prevList => prevList.filter(errorId => !idsToRemoveSet.has(errorId)))
            }
        }
        // Nollställer beroende svar framåt i flödet när en förälder ändras
        dispatch(resetDependentAnswersForward(questionObject))

        //Används för att nollställa vilka frågor som är markerade som visade innan en ny renderingsomgång.
        //Endast frågor som faktiskt visas i UI:t kommer senare få isDisplayed = true igen.
        dispatch(clearDisplayedFromStep({ activeStep }))
    }

    /**
     * Skapar det dataobjekt som ska skickas till API:t och postar ärendet till backend.
     * Förhindrar webbläsarens standardbeteende (sidomladdning vid formulärsubmit)
     * genom att använda preventDefault, så att React kan hantera hela submit-flödet.
     */
    const onSubmitHandler = (e: any) => {
        e.preventDefault()
        const submitData = createApiDataObject(globalQuestions, specialMappedQuestions, translatedAnswers)
        dispatch(
            submitApplication({
                submitData,
                url: appConfig.postToServerUrl,
            })
        )
    }

    /**
     * Startar formuläret och flyttar fokus till h1-rubriken i steg 1.
     */
    const startApplicationButtonHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()

        setActiveStep(1)
        focusElement('pts-main-service-headline')
    }

    /**
     * Validerar frågorna i aktuellt steg, uppdaterar validerings-fel-listan och går vidare till
     * nästa steg i formuläret.
     * - Vid inga fel: går vidare till nästa steg och sätter fokus på h1-rubiken. Om nästa steg är
     * det sista steget sätts formuläret i förhandsgranskningsläge.
     * - Vid fel: behåller steget och sätter fokus på validerings-fel-sammanfattningen.
     */
    const nextStepButtonHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        const currentStepQuestions = stepCategoryGroupStructure[activeStep - 1]
        const validationErrorCheckResult = findVisibleQuestionsWithValidationErrors(
            currentStepQuestions,
            globalQuestions
        )
        const hasNoValidationErrors = validationErrorCheckResult.length === 0

        setValidationErrorsList(validationErrorCheckResult)

        if (hasNoValidationErrors) {
            const nextStep = activeStep + 1
            const isLastStep = nextStep === globalSteps.length

            setShowPreview(isLastStep)
            setActiveStep(nextStep)
            focusElement('pts-main-service-headline')
        } else {
            focusElement('pts-error-summary')
        }
    }

    /**
     * Byter aktivt steg, stänger av förhandsvisningen
     * och flyttar fokus till h1-rubiken på det nya steget.
     */
    const changeStepHandler = (step: number) => {
        setActiveStep(step)
        setShowPreview(false)
        setValidationErrorsList([])
        focusElement('pts-main-service-headline')
    }

    /**
     * Sätter fokus på huvudrubriken när formuläret skickats in med lyckat resultat.
     */
    useEffect(() => {
        if (globalFormStatus === 'failed') {
            logToSeq({
                message: `SessionId: ${sessionId}, Form submission failed`,
                level: 'Error',
            })
        } else if (globalFormStatus === 'success') {
            logToSeq({
                message: `SessionId: ${sessionId}, Form submitted successfully`,
                level: 'Information',
            })
        }
        if (globalFormStatus === 'success') {
            focusElement('pts-main-service-headline')
        }
    }, [globalFormStatus])

    /**
     * Bygger upp en hierarkisk datastruktur för formuläret, grupperad
     * per steg, kategori och frågegrupp. Bygger om när frågor, steg
     * eller valideringsfel uppdateras.
     */
    useEffect(() => {
        setStepCategoryGroupStructure(
            buildStepCategoryGroupQuestionStructure(globalQuestions, globalSteps, validationErrorsList)
        )
    }, [globalQuestions, globalSteps, validationErrorsList])

    return (
        <>
            <PTSFormRenderer
                globalActivatedLanguage={globalActivatedLanguage}
                globalApplicationContent={globalApplicationContent}
                globalLanguageState={globalLanguageState}
                globalSteps={globalSteps}
                globalQuestions={globalQuestions}
                globalFormStatus={globalFormStatus}
                globalThankYouPageContent={globalThankYouPageContent}
                showPreview={showPreview}
                activeStep={activeStep}
                currentStepQuestions={stepCategoryGroupStructure[activeStep - 1]}
                previewPageData={previewPageData}
                validationErrorsList={validationErrorsList}
                handleQuestionInputChange={handleQuestionInputChange}
                onSubmitHandler={onSubmitHandler}
                nextStepButtonHandler={nextStepButtonHandler}
                changeStepHandler={changeStepHandler}
                startApplicationButtonHandler={startApplicationButtonHandler}
                markQuestionAsDisplayed={markQuestionAsDisplayed}
            />

            <FormStatusMessagesScreenReader formStatus={globalFormStatus} activatedLanguage={globalActivatedLanguage} />
        </>
    )
}

export default PTSForm
